<script lang="ts" setup>
// Core
import { computed, ref } from "vue";

// Libraries
import { TYPE } from "vue-toastification";
import { useElementSize } from "@vueuse/core";
import InlineSvg from "vue-inline-svg";

// Assets
import updateIconUrl from "@/assets/icons/toast/success_icon.svg?url";

interface Props {
  type?: TYPE;
  title?: string;
  message?: string;
  updateAction?: () => void;
  laterAction?: () => void;
}

const props = withDefaults(defineProps<Props>(), {
  type: TYPE.INFO,
  title: "Nova versão disponível",
  message: "Uma nova versão do aplicativo está disponível. Atualize para obter os recursos mais recentes."
});

const emit = defineEmits(["close-toast"]);

const toastRef = ref<HTMLElement | null>(null);
const { height } = useElementSize(toastRef);

const handleUpdate = () => {
  if (props.updateAction) {
    props.updateAction();
  }
  emit("close-toast");
};

const handleLater = () => {
  if (props.laterAction) {
    props.laterAction();
  }
  emit("close-toast");
};

const toastStatus = computed(() => ({
  type: "info",
  icon: updateIconUrl
}));
</script>

<template>
  <div
    ref="toastRef"
    :class="['update-toast', `update-toast--${toastStatus.type}`]"
    role="dialog"
    aria-live="polite"
    aria-labelledby="update-title"
    aria-describedby="update-message"
  >
    <div :style="`height: ${height}px`" class="update-toast__type">
      <div class="update-toast__type__backdrop" />
      <inline-svg :src="toastStatus.icon" />
    </div>
    
    <div class="update-toast__body">
      <h2 id="update-title" class="update-toast__body__title">{{ props.title }}</h2>
      <p id="update-message" v-if="props.message" v-html="props.message" />
      
      <div class="update-toast__actions">
        <button 
          class="update-toast__action-btn update-toast__action-btn--primary"
          @click="handleUpdate"
          aria-label="Atualizar aplicativo agora"
        >
          Atualizar agora
        </button>
        <button 
          class="update-toast__action-btn update-toast__action-btn--secondary"
          @click="handleLater"
          aria-label="Atualizar mais tarde"
        >
          Mais tarde
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.update-toast {
  background: $extra_light_gray_bg;
  box-shadow: $toast_box_shadow;
  border-radius: $br_sm;
  position: relative;
  min-height: auto;
  max-width: 420px;
  min-width: 380px;
  @include flex(row, center, start);
  padding: 0;

  &.update-toast--info .update-toast__type .update-toast__type__backdrop {
    background: #3B82F6;

    span:deep(svg path) {
      fill: #FFFFFF;
    }
  }

  &__type {
    min-width: $toast_type_backdrop_width;
    height: 100%;
    border-radius: $br_sm;
    overflow: hidden;
    position: relative;
    @include flex-center;

    &__backdrop {
      width: 100%;
      height: 100%;
      position: absolute;
      border-radius: 0px 70% 70% 0px;
      opacity: 0.3;
      z-index: $null_index;
    }

    > span {
      position: relative;
      z-index: $base_index;
    }
  }

  &__body {
    @include flex(column, start, start);
    padding: $spacing_md_plus;
    gap: $spacing_sm;
    flex: 1;

    h2 {
      @include break-text;
      font-size: $fs_sm;
      font-weight: $fw_bd;
      color: $text_neutral_gray;
      margin: 0;
    }

    p {
      @include break-text;
      font-size: $fs_sm;
      color: $text_neutral_gray;
      margin: 0 0 $spacing_sm 0;
      line-height: 1.4;
    }
  }

  &__actions {
    @include flex(row, center, center);
    gap: $spacing_sm;
    width: 100%;
    margin-top: $spacing_sm;
  }

  &__action-btn {
    @include reset-button;
    padding: $spacing_sm $spacing_md;
    border-radius: $br_xs;
    font-size: $fs_xs;
    font-weight: $fw_md;
    cursor: pointer;
    transition: all 0.2s ease;
    flex: 1;
    min-height: 36px;

    &--primary {
      background: #3B82F6;
      color: white;
      border: 1px solid #3B82F6;

      &:hover {
        background: #2563EB;
        border-color: #2563EB;
      }

      &:active {
        background: #1D4ED8;
      }
    }

    &--secondary {
      background: transparent;
      color: $text_neutral_gray;
      border: 1px solid #E5E7EB;

      &:hover {
        background: #F9FAFB;
        border-color: #D1D5DB;
      }

      &:active {
        background: #F3F4F6;
      }
    }
  }
}
</style>